import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { itemsOf, listContracts, type PAContract } from '../api/money';
import { usePAQuery } from './usePAQuery';

/**
 * THE CONTRACT PICKER'S STATE — read once, shared by every money screen.
 *
 * # Why it is a context and not a per-screen fetch
 *
 * Appendix L §2.3: «A Subscriber may have several Contracts, each with its own independent
 * balance», and §6, §9, §19, §20, §22 and §23 all begin with the same «Выберите договор»
 * dropdown. If each screen fetched and chose for itself, moving from the dashboard to the
 * payment history would silently jump back to the default contract — so a subscriber
 * looking at the SECOND contract's balance would be shown the FIRST contract's payments,
 * with both screens labelled correctly and neither of them wrong on its own. That is the
 * shape of defect this project keeps finding in seams, so the selection is ONE value.
 *
 * # It is state, not storage
 *
 * A reload returns to the default contract, and that is deliberate rather than a
 * limitation: `src/storage.test.ts` forbids `localStorage` in this app for reasons a shared
 * phone makes concrete, and a remembered contract id is not worth an exception.
 * `contract.default_in_personal_area` is the operator's own answer to «which one should
 * open», so the reset lands where staff intended.
 *
 * # The one-Contract case
 *
 * ⚠ A subscriber with ONE contract must not see a chooser. Most subscribers have exactly
 * one, and a dropdown with a single option is the D109 dead control this phase has already
 * removed twice (the language switch, the recovery-channel radio). [ContractPicker] renders
 * nothing at all in that case; the contract's number is shown as a fact on the dashboard
 * instead.
 */

export interface ContractApi {
  /** `empty` is a real state: a live card with no contract, which is not an error. */
  status: 'loading' | 'ready' | 'error' | 'empty';
  error: unknown;
  contracts: PAContract[];
  /** The contract every money screen reads, or null while loading / on error / if none. */
  selected: PAContract | null;
  select: (id: number) => void;
  reload: () => void;
}

const ContractContext = createContext<ContractApi | null>(null);

export function useContracts(): ContractApi {
  const ctx = useContext(ContractContext);
  if (!ctx) throw new Error('useContracts must be used inside <ContractProvider>');
  return ctx;
}

export function ContractProvider({ children }: { children: ReactNode }) {
  const { query, reload } = usePAQuery<PAContract[]>(
    async () => itemsOf(await listContracts()),
    [],
  );
  // The explicit choice, when the subscriber has made one. `null` means «whatever the
  // operator marked as default», re-resolved from the freshly loaded list — so a reload
  // never leaves the picker pointing at a contract that is no longer served.
  const [chosen, setChosen] = useState<number | null>(null);

  const value = useMemo<ContractApi>(() => {
    const contracts = query.status === 'ready' ? query.data : [];
    /*
      THE SELECTION, in the order the operator's own intent survives.

      ⚠ THE THIRD CLAUSE IS NEW (2026-08-12) AND IT IS NOT COSMETIC. `ownedContract`
      admits a TERMINATED contract — it refuses only archived and deleted ones — while
      `default_in_personal_area` is carried by NO contract in this database, so the
      fallback did all the work and the fallback is `contracts[0]`, which the store
      orders by `id ASC`: THE OLDEST. A subscriber whose first contract was closed years
      ago therefore opened his cabinet on it. Preferring a live contract when the
      operator has expressed no preference is the honest default; a terminated contract
      is still SELECTABLE (a subscriber plausibly wants last year's invoices) and is now
      labelled everywhere it appears, which is the other half of the same fix.
    */
    const selected =
      contracts.find((c) => c.id === chosen) ??
      contracts.find((c) => c.is_default) ??
      contracts.find((c) => c.status !== 'terminated') ??
      contracts[0] ??
      null;
    let status: ContractApi['status'] = 'loading';
    if (query.status === 'error') status = 'error';
    else if (query.status === 'ready') status = contracts.length > 0 ? 'ready' : 'empty';
    return {
      status,
      error: query.status === 'error' ? query.error : null,
      contracts,
      selected,
      select: setChosen,
      reload,
    };
  }, [query, chosen, reload]);

  return <ContractContext.Provider value={value}>{children}</ContractContext.Provider>;
}
